import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { useFilterState } from '../hooks/useFilterState';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Search, RefreshCw, Globe, Info, ExternalLink, AlertCircle, Sparkles } from 'lucide-react';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';

const POPULAR_COMMODITIES = [
  'Tomato', 'Onion', 'Potato', 'Banana', 'Apple', 'Rice', 'Wheat',
  'Chilli', 'Garlic', 'Ginger', 'Cauliflower', 'Cabbage', 'Carrot',
  'Mango', 'Brinjal', 'Capsicum', 'Coriander', 'Mint', 'Lemon',
  'Sugar', 'Coconut', 'Spinach', 'Cucumber', 'Beans'
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Keralam', 'Madhya Pradesh', 'Maharashtra', 'Odisha', 'Punjab', 'Rajasthan',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

export const MandiPrices = () => {
  const { API, selectedClient } = useClient();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  // Persist filters across navigation
  const [mandiFilters, setMandiFilters] = useFilterState('mandi', selectedClient?.id, {
    commodity: 'Tomato', state: ''
  });
  const commodity = mandiFilters.commodity;
  const state = mandiFilters.state;
  const setCommodity = (v) => setMandiFilters({ ...mandiFilters, commodity: v });
  const setState = (v) => setMandiFilters({ ...mandiFilters, state: v });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  const fetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (commodity) params.set('commodity', commodity);
      if (state) params.set('state', state);
      params.set('limit', '100');
      
      const res = await axios.get(`${API}/mandi-prices?${params}`);
      setRecords(res.data.records || []);
      setMeta({
        configured: res.data.configured,
        total: res.data.total,
        fetched_at: res.data.fetched_at,
        error: res.data.error,
        message: res.data.message
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch mandi prices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // Calculate stats
  const stats = records.length > 0 ? {
    min: Math.min(...records.map(r => r.modal_price).filter(p => p > 0)),
    max: Math.max(...records.map(r => r.modal_price).filter(p => p > 0)),
    avg: Math.round(records.reduce((s, r) => s + r.modal_price, 0) / records.length),
    count: records.length
  } : null;

  return (
    <div className="space-y-6" data-testid="mandi-prices-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Live APMC Market Prices</h2>
          <p className="text-text-secondary mt-1">
            Reference only · wholesale rates from data.gov.in Agmarknet.{' '}
            <span className="text-text-muted">These are not benchmarked against your invoices — we're not doing that yet.</span>
          </p>
        </div>
        <Button onClick={fetch} disabled={loading} className="bg-primary hover:bg-primary-hover" data-testid="refresh-mandi">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* AI Upload section — operator only (clients get read-only market data) */}
      {selectedClient && !isClient && (
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-md p-4 flex items-start space-x-3">
            <Sparkles className="h-5 w-5 text-state-info flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-semibold text-primary mb-1">Upload your own price list</p>
              <p className="text-text-secondary text-xs">
                Upload a PDF, Excel, or image of a mandi/wholesale price sheet — AI will extract every row and tag prices to <strong>{selectedClient.name}</strong>.
              </p>
            </div>
          </div>
          <ModuleUploadZone
            module="mandi"
            moduleLabel="Mandi / Wholesale Price List"
            clientId={selectedClient.id}
            API={API}
          />
          <PendingImports clientId={selectedClient.id} API={API} module="mandi" />
        </div>
      )}

      {/* Data Source Notice */}
      <div className="bg-blue-50 border-l-4 border-state-info rounded-md p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-state-info flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-semibold text-state-info mb-1">Where does this data come from?</p>
            <p className="text-text-secondary text-xs">
              Live data from <a href="https://data.gov.in/resource/current-daily-price-various-commodities-various-markets-mandi" target="_blank" rel="noopener" className="text-state-info underline inline-flex items-center">data.gov.in Agmarknet <ExternalLink className="h-3 w-3 ml-1" /></a> — 
              the official Ministry of Agriculture API providing daily wholesale mandi prices from APMC markets across India. 
              Prices are in <strong>₹ per quintal</strong> (100 kg). Divide by 100 for per-kg rate.
            </p>
            {meta?.fetched_at && (
              <p className="text-xs text-text-muted mt-1">Last fetched: {new Date(meta.fetched_at).toLocaleString()}</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-md p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">Commodity</label>
            <select
              value={commodity}
              onChange={(e) => setCommodity(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2"
              data-testid="commodity-select"
            >
              <option value="">All commodities</option>
              {POPULAR_COMMODITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">State</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2"
              data-testid="state-select"
            >
              <option value="">All states</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={fetch} disabled={loading} className="w-full bg-primary hover:bg-primary-hover" data-testid="search-mandi">
              <Search className="mr-2 h-4 w-4" /> Search Prices
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && commodity && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Markets Reporting" value={stats.count} />
          <StatCard label="Min Modal Price" value={`₹${stats.min}`} sublabel="per quintal" />
          <StatCard label="Max Modal Price" value={`₹${stats.max}`} sublabel="per quintal" />
          <StatCard label="Average Modal" value={`₹${stats.avg}`} sublabel={`≈ ₹${(stats.avg/100).toFixed(2)}/kg`} highlight />
        </div>
      )}

      {/* Error / Empty state */}
      {meta && !meta.configured && (
        <div className="bg-yellow-50 border-l-4 border-state-warning rounded-md p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-state-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-state-warning text-sm">API Key Not Configured</p>
            <p className="text-xs text-text-secondary mt-1">{meta.message}</p>
          </div>
        </div>
      )}

      {meta?.error && (
        <div className="bg-red-50 border-l-4 border-state-error rounded-md p-4">
          <p className="text-sm text-state-error">{meta.error}</p>
        </div>
      )}

      {/* Prices Table */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-primary text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Commodity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Variety</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Market</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">State</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Min ₹/Q</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Modal ₹/Q</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Max ₹/Q</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">≈ ₹/kg</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan="9" className="text-center text-text-muted py-8">Loading prices...</td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan="9" className="text-center text-text-muted py-8">No prices found. Try different filters.</td></tr>
              )}
              {!loading && records.map((r, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`mandi-row-${idx}`}>
                  <td className="px-4 py-3 text-sm font-medium text-text-primary">{r.commodity}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{r.variety || '-'}</td>
                  <td className="px-4 py-3 text-sm">{r.market}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{r.state}</td>
                  <td className="px-4 py-3 text-sm text-right text-text-muted">₹{r.min_price.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-primary">₹{r.modal_price.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-right text-text-muted">₹{r.max_price.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-right text-accent font-medium">₹{(r.modal_price / 100).toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-text-muted">{r.arrival_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sublabel, highlight }) => (
  <div className={`bg-white border ${highlight ? 'border-accent' : 'border-border'} rounded-md p-4`}>
    <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">{label}</p>
    <p className={`text-2xl font-bold ${highlight ? 'text-accent' : 'text-primary'}`}>{value}</p>
    {sublabel && <p className="text-xs text-text-muted mt-1">{sublabel}</p>}
  </div>
);
